export type ScheduledOfferState = 'active' | 'inactive';

export type ScheduledOfferPromoType =
  | 'price'
  | 'buy-x-get-y'
  | 'nth-unit-discount'
  | 'product-discount'
  | 'free-gift'
  | 'gift-with-purchase'
  | 'free-shipping'
  | 'early-access';

/** Mirrors ProductPriceSchedule.DISCOUNT_MODE_CHOICES. */
export type ScheduledOfferDiscountMode = 'relative' | 'absolute';

/** One product of an early-access offer and its own percentage off. */
export interface ScheduledOfferItem {
  product: number;
  /** Read-only: filled in by the API. */
  product_name?: string;
  discount_value: string;
}

export interface ScheduledOfferModel {
  id: number;
  promo_type: ScheduledOfferPromoType;
  promo_type_display: string;
  name: string;
  product: number | null;
  product_name: string | null;
  store: number;
  store_name: string;
  currency_symbol: string;
  price: string | null;
  buy_qty: number | null;
  free_qty: number | null;
  nth_unit: number | null;
  discount_mode: ScheduledOfferDiscountMode | null;
  discount_value: string | null;
  /** Early access only: the discounted products. */
  items: ScheduledOfferItem[];
  early_access_from: string | null;
  early_access_until: string | null;
  early_access_gift_product: number | null;
  early_access_gift_product_name: string | null;
  valid_from: string;
  valid_until: string;
  max_uses_per_user: number;
  /** Gift with purchase only: every product of these categories counts. */
  condition_categories: number[];
  /** Gift with purchase only: product variants that count for the condition. */
  condition_products: number[];
  state: ScheduledOfferState;
  created_at: string;
  updated_at: string;
}

export interface ScheduledOfferPayload {
  promo_type: ScheduledOfferPromoType;
  name: string;
  product: number | null;
  store: number;
  price: string | null;
  buy_qty: number | null;
  free_qty: number | null;
  nth_unit: number | null;
  discount_mode: ScheduledOfferDiscountMode | null;
  discount_value: string | null;
  items: Omit<ScheduledOfferItem, 'product_name'>[];
  early_access_from: string | null;
  early_access_until: string | null;
  early_access_gift_product: number | null;
  valid_from: string;
  valid_until: string;
  max_uses_per_user: number;
  condition_categories: number[];
  condition_products: number[];
  state: ScheduledOfferState;
}

interface PromoTypeFields {
  product: boolean;
  price: boolean;
  quantities: boolean;
  /** Which unit gets discounted, for "Nth unit at X% off". */
  nthUnit: boolean;
  /** Discount mode + value, shared by the types that discount units. */
  discount: boolean;
  /**
   * Store-wide types (free shipping) apply per order, and early access has no
   * per-customer limit, so neither has a per-user cap.
   */
  maxUses: boolean;
  /** Several products, each with its own percentage off, for early access. */
  items: boolean;
  /**
   * Subscribers-only window inside the offer that adds a gift. Required by
   * early-access offers and rejected by the backend on any other type.
   */
  earlyAccess: boolean;
  /**
   * Categories and/or products whose units must reach buy_qty to trigger the
   * gift, for "gift with purchase". Here `product` is the gift and `free_qty`
   * its units.
   */
  conditionProducts: boolean;
}

/** Fields every type leaves unset; each entry below only lists what it uses. */
const NO_FIELDS: PromoTypeFields = {
  product: false,
  price: false,
  quantities: false,
  nthUnit: false,
  discount: false,
  maxUses: false,
  items: false,
  earlyAccess: false,
  conditionProducts: false,
};

/**
 * Which fields each promo type uses. Mirrors REQUIRED_BY_TYPE in
 * stores/models.py (and _validate_items() in its API serializer): the backend
 * rejects a payload carrying a field that does not belong to its type, so the
 * form must clear them when the type changes.
 */
export const PROMO_TYPE_FIELDS: Record<
  ScheduledOfferPromoType,
  PromoTypeFields
> = {
  price: { ...NO_FIELDS, product: true, price: true, maxUses: true },
  'buy-x-get-y': {
    ...NO_FIELDS,
    product: true,
    quantities: true,
    maxUses: true,
  },
  'nth-unit-discount': {
    ...NO_FIELDS,
    product: true,
    nthUnit: true,
    discount: true,
    maxUses: true,
  },
  'product-discount': {
    ...NO_FIELDS,
    product: true,
    discount: true,
    maxUses: true,
  },
  'free-gift': { ...NO_FIELDS, product: true, maxUses: true },
  'gift-with-purchase': {
    ...NO_FIELDS,
    product: true,
    quantities: true,
    maxUses: true,
    conditionProducts: true,
  },
  'free-shipping': NO_FIELDS,
  'early-access': { ...NO_FIELDS, items: true, earlyAccess: true },
};

/**
 * Whether the offer carries an early-access window, which only early-access
 * offers do. Any of the three fields counts, so a half-configured one coming
 * from the API still shows up.
 */
export function hasEarlyAccess(offer: ScheduledOfferModel): boolean {
  return Boolean(
    offer.early_access_from ||
      offer.early_access_until ||
      offer.early_access_gift_product
  );
}

/**
 * Types offered when creating an offer, in the order they appear in the form.
 *
 * Free shipping is hidden for now: the backend still supports it and existing
 * offers keep being applied, so re-enabling it is just uncommenting the line.
 * Everything else about the type (its union member, its entry in
 * PROMO_TYPE_FIELDS and its label) stays in place.
 */
export const PROMO_TYPES: ScheduledOfferPromoType[] = [
  'price',
  'buy-x-get-y',
  'nth-unit-discount',
  'product-discount',
  'early-access',
  'free-gift',
  'gift-with-purchase',
  // 'free-shipping',
];

export const PROMO_TYPE_LABEL_IDS: Record<ScheduledOfferPromoType, string> = {
  price: 'scheduledOffers.promoType.price',
  'buy-x-get-y': 'scheduledOffers.promoType.buyXGetY',
  'nth-unit-discount': 'scheduledOffers.promoType.nthUnitDiscount',
  'product-discount': 'scheduledOffers.promoType.productDiscount',
  'free-gift': 'scheduledOffers.promoType.freeGift',
  'gift-with-purchase': 'scheduledOffers.promoType.giftWithPurchase',
  'free-shipping': 'scheduledOffers.promoType.freeShipping',
  'early-access': 'scheduledOffers.promoType.earlyAccess',
};
